import React from 'react';
import { StyleSheet, View } from 'react-native';
import type { InventoryItemResponse } from '../../../api/types';
import { ItemCard, ItemCardSkeleton } from './ItemCard';

/**
 * Vertical list of item cards — one per detected item. The actual
 * card shape is factored into {@link ItemCard} so this surface and
 * the {@link FocusedItemCarousel} render pixel-identically with no
 * style fork.
 *
 * When `focusedItemId` is set, that card floats to the top of the
 * list so the rail matches the currently-focused bbox on the stage.
 */

export interface ItemRailItem {
  item: InventoryItemResponse;
  selected: boolean;
  hasMask: boolean;
  /** True when this item had a `mask_error` SSE event. */
  maskErrored?: boolean;
  /** True after a successful /resegment — surfaces the IoU pill. */
  corrected?: boolean;
  /** IoU between the original SAM3 mask and the user-corrected one. */
  iou?: number;
  /** Brief glow pulse when the matching bbox chip was tapped. */
  highlighted?: boolean;
}

interface ItemRailProps {
  items: ItemRailItem[];
  onToggleSelected: (id: number) => void;
  onEditDetails: (id: number) => void;
  onRefineBbox: (id: number) => void;
  onDeleteItem: (id: number) => void;
  onAnnotate?: (id: number) => void;
  streaming: boolean;
  focusedItemId?: number | null;
  /** #169 C1 — read-only saved-scene mode (hides scan-triage chips,
   *  keeps select-to-share). Default false = live-scan behavior. */
  readOnly?: boolean;
  /** #170-followup — in read-only mode, tapping a card opens its detail. */
  onItemPress?: (item: InventoryItemResponse) => void;
}

export const ItemRail: React.FC<ItemRailProps> = ({
  items,
  onToggleSelected,
  onEditDetails,
  onRefineBbox,
  onDeleteItem,
  onAnnotate,
  streaming,
  focusedItemId,
  readOnly = false,
  onItemPress,
}) => {
  // Pin the focused item's card to the top.
  const ordered =
    focusedItemId !== null && focusedItemId !== undefined
      ? [
          ...items.filter((it) => it.item.id === focusedItemId),
          ...items.filter((it) => it.item.id !== focusedItemId),
        ]
      : items;

  return (
    <View style={styles.rail}>
      {ordered.map((entry) => {
        const id = entry.item.id ?? -1;
        return (
          <ItemCard
            key={`rail-${id}`}
            item={entry.item}
            selected={entry.selected}
            hasMask={entry.hasMask}
            maskErrored={entry.maskErrored}
            corrected={entry.corrected}
            iou={entry.iou}
            highlighted={entry.highlighted}
            streaming={streaming}
            onToggleSelected={onToggleSelected}
            onEditDetails={onEditDetails}
            onRefineBbox={onRefineBbox}
            onDeleteItem={onDeleteItem}
            onAnnotate={onAnnotate}
            readOnly={readOnly}
            onItemPress={onItemPress}
          />
        );
      })}
      {streaming ? <ItemCardSkeleton /> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  rail: {
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 24,
  },
});
